import { Component, OnInit } from '@angular/core';
import { SettingsService } from 'src/app/services/settings.service';
import { GlobalServicesService } from 'src/app/services/global-services.service';
import { MessageService } from 'primeng/api';

interface ConferenceProvider {
    name: string;
    key: string;
    category: string;
    is_integrated: boolean;
    is_disabled?: boolean;
    oauth_error?: string | null;
}

interface ProviderProfile {
    display_name: string;
    email: string;
}

@Component({
    selector: 'app-integrations',
    templateUrl: './app-integrations.component.html',
    styleUrls: ['./app-integrations.component.css'],
    standalone: false
})
export class AppIntegrationsComponent implements OnInit {
    providers: ConferenceProvider[] = [];
    isLoading = true;

    // Dialog states
    showDetailsDialog = false;
    showRevokeDialog = false;
    selectedProvider: ConferenceProvider | null = null;
    providerProfile: ProviderProfile | null = null;
    isLoadingProfile = false;
    isRevoking = false;

    // User privileges
    userPrivileges: Set<string> = new Set();

    // Icon mapping based on provider key
    private iconMap: { [key: string]: string } = {
        'default': 'assets/icons-svg/Interview Icon.svg',
        'ms_teams': 'assets/icons-svg/teams.svg',
        'google_meet': 'assets/icons-svg/Gmeet.svg',
        'zoom': 'assets/icons-svg/zoom.svg'
    };

    // Description mapping based on provider key
    private descriptionMap: { [key: string]: string } = {
        'default': 'Built-in video conferencing provider, always available',
        'ms_teams': 'Connect with Microsoft Teams for video conferencing and collaboration',
        'google_meet': 'Integrate Google Meet for seamless video meetings',
        'zoom': 'Connect Zoom for enterprise video conferencing'
    };

    constructor(
        private settingsService: SettingsService,
        private globalService: GlobalServicesService,
        private messageService: MessageService
    ) { }

    ngOnInit(): void {
        this.globalService.privilegeDetailsSubject.subscribe((privileges) => {
            this.userPrivileges = privileges;
        });
        this.loadProviders();
    }

    loadProviders(): void {
        this.isLoading = true;

        // Default Provider - always connected
        const defaultProvider: ConferenceProvider = {
            name: 'Default Provider',
            key: 'default',
            category: 'conferencing',
            is_integrated: true
        };

        this.settingsService.getConferenceProviders().subscribe({
            next: (res: any) => {
                if (res.status === 200 && res.body?.items) {
                    this.providers = [defaultProvider, ...res.body.items];
                } else {
                    this.providers = [defaultProvider];
                }
                this.isLoading = false;
            },
            error: () => {
                this.providers = [defaultProvider];
                this.isLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load app integrations'
                });
            }
        });
    }

    getProviderIcon(key: string): string {
        return this.iconMap[key] || 'assets/icons-svg/configuration.svg';
    }

    getProviderDescription(key: string): string {
        return this.descriptionMap[key] || 'Connect this app for seamless integration';
    }

    getDisabledReason(provider: ConferenceProvider): string {
        if (!provider.is_disabled) return '';        
        const connectedProvider = this.providers.find(p => 
            p.is_integrated && 
            p.key !== provider.key && 
            p.key !== 'default' &&
            (p.key === 'ms_teams' || p.key === 'google_meet')
        );
        
        if (connectedProvider) {
            return `${connectedProvider.name} is already connected. Disconnect it first to connect ${provider.name}.`;
        }
        return 'Another provider is already connected';
    }

    onConnect(provider: ConferenceProvider): void {
        this.settingsService.connectConferenceProvider(provider.key).subscribe({
            next: (res: any) => {
                if (res.status === 200) {
                    const oauthUrl = res.body?.oauth_url;
                    if (oauthUrl) {
                        window.open(oauthUrl, '_blank');
                    }
                }
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to initiate connection'
                });
            }
        });
    }

    onShowDetails(provider: ConferenceProvider): void {
        this.selectedProvider = provider;
        this.providerProfile = null;
        this.showDetailsDialog = true;
        this.loadProviderProfile(provider.key);
    }

    loadProviderProfile(providerKey: string): void {
        this.isLoadingProfile = true;
        this.settingsService.getConferenceProviderProfile(providerKey).subscribe({
            next: (res: any) => {
                if (res.status === 200 && res.body) {
                    this.providerProfile = res.body;
                }
                this.isLoadingProfile = false;
            },
            error: () => {
                this.isLoadingProfile = false;
            }
        });
    }

    onShowRevokeConfirm(provider: ConferenceProvider): void {
        this.selectedProvider = provider;
        this.providerProfile = null;
        this.showRevokeDialog = true;
        this.loadProviderProfile(provider.key);
    }

    onConfirmRevoke(): void {
        if (!this.selectedProvider) return;

        this.isRevoking = true;
        this.settingsService.revokeConferenceProvider(this.selectedProvider.key).subscribe({
            next: (res: any) => {
                if (res.status === 200) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: `${this.selectedProvider?.name} has been disconnected`
                    });
                    this.loadProviders();
                }
                this.isRevoking = false;
                this.showRevokeDialog = false;
                this.selectedProvider = null;
            },
            error: () => {
                this.isRevoking = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to revoke connection'
                });
            }
        });
    }

    onCancelRevoke(): void {
        this.showRevokeDialog = false;
        this.selectedProvider = null;
    }

    closeDetailsDialog(): void {
        this.showDetailsDialog = false;
        this.selectedProvider = null;
        this.providerProfile = null;
    }
}
